import { FC, useEffect, useState } from "react";
import { useTrans } from "../../../hooks";
import {
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    SimpleGrid,
    VStack,
} from "@chakra-ui/react";
import {
    Company,
    CompanyApi,
    Training,
    TrainingApi,
    Typology,
    TypologyApi,
} from "../../../apis";
import { Controller, useForm } from "react-hook-form";
import { AppFilterContainer } from "../../../containers";
import {
    AppIcon,
    AppDatePicker,
    AppFormControl,
    AppMultiSelector,
    AppMultiSelectorOption,
    AppMultiSelectorOptionType,
    AppSelectOption,
    AppSelect,
} from "../../../components";
import { APPC, UTIL } from "../../../config";

export type DateRangeType = [Date | null, Date | null];

export type MyInternshipFilterDataType = {
    internship_schedule_seat_search: string;
    startDateRange: DateRangeType;
    endDateRange: DateRangeType;
    training: string;
    typology: string[];
};

const defaultValues: MyInternshipFilterDataType = {
    internship_schedule_seat_search: "",
    startDateRange: [null, null],
    endDateRange: [null, null],
    training: "",
    typology: [],
};

export const MyInternshipFilter: FC<{
    filterData: any;
    setFilterData: (data: any) => void;
    children: JSX.Element;
}> = ({ filterData, setFilterData, children }) => {
    // hook
    const { t } = useTrans();

    // state & const
    const [isInit, setIsInit] = useState(false);
    const [selectedCompanies, setSelectedCompanies] = useState<
        AppMultiSelectorOption[]
    >([]);
    const [loadingTraining, setLoadingTraining] = useState(true);
    const [trainingOptions, setTrainingOptions] = useState<AppSelectOption[]>(
        []
    );
    const [selectedTraining, setSelectedTraining] = useState<AppSelectOption>();
    const [loadingTypology, setLoadingTypology] = useState(true);
    const [typologyOptions, setTypologyOptions] = useState<AppSelectOption[]>(
        []
    );
    const [selectedTypologies, setSelectedTypologies] = useState<
        AppSelectOption[]
    >([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const dayjs = UTIL.getDayJs();

    const { handleSubmit, reset, control } =
        useForm<MyInternshipFilterDataType>({
            mode: "onSubmit",
        });

    // handler
    const onSubmitHandler = (formData: MyInternshipFilterDataType) => {
        const filterParams: Record<string, string | string[]> = {};

        if (formData.internship_schedule_seat_search) {
            filterParams["internship_schedule_seat_search"] =
                formData.internship_schedule_seat_search;
        }

        // startDateRange transformation
        if (formData.startDateRange[0]) {
            filterParams["internshipSchedule.startDate[after]"] = dayjs(
                formData.startDateRange[0]
            ).format(APPC.APP_HTML_DATE_FORMAT);
        }
        if (formData.startDateRange[1]) {
            filterParams["internshipSchedule.startDate[before]"] = dayjs(
                formData.startDateRange[1]
            ).format(APPC.APP_HTML_DATE_FORMAT);
        }

        // endDateRange transformation
        if (formData.endDateRange[0]) {
            filterParams["internshipSchedule.endDate[after]"] = dayjs(
                formData.endDateRange[0]
            ).format(APPC.APP_HTML_DATE_FORMAT);
        }
        if (formData.endDateRange[1]) {
            filterParams["internshipSchedule.endDate[before]"] = dayjs(
                formData.endDateRange[1]
            ).format(APPC.APP_HTML_DATE_FORMAT);
        }

        // company transformation
        if (selectedCompanies.length > 0) {
            filterParams["internship.company.id"] = selectedCompanies?.map(
                (item) => item.value
            );
        }

        if (formData.training) {
            filterParams["internship.training.id"] = formData.training;
        }

        if (formData.typology && formData.typology.length > 0) {
            filterParams["internship.typology.id"] = formData.typology?.map(
                (item) => item
            );
        }

        setFilterData(filterParams);
    };

    const loadCompanyOptions = async (
        page: number,
        search: string,
        pageSize: number
    ): Promise<AppMultiSelectorOptionType> => {
        const params = {
            itemsPerPage: pageSize,
            [`order[name]`]: "asc",
            pagination: true,
        };

        if (search) {
            params["name"] = search;
        }

        const { response } = await CompanyApi.getCollectionMyCompanies<Company>(
            page,
            params
        );

        if (response !== null) {
            const newOptions =
                response.items?.map((e) => ({
                    value: CompanyApi.toResourceIRI(e.id),
                    label: `${e.id} ${e.name}`,
                })) || [];
            return {
                data: newOptions,
                totalItems: response.totalItems,
            };
        }

        return { data: [], totalItems: 0 };
    };

    const loadTrainingOptions = () => {
        setLoadingTraining(true);
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
        };

        TrainingApi.getCollectionForSelect<Training>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    const options = response.items.map((training) => ({
                        value: TrainingApi.toResourceIRI(training.id),
                        label: training.name,
                    }));
                    setTrainingOptions(options);
                }
            })
            .finally(() => setLoadingTraining(false));
    };

    const loadTypologyOptions = () => {
        setLoadingTypology(true);
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
            "training.id": `${selectedTraining?.value}`,
        };

        TypologyApi.getCollectionForSelect<Typology>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    const options = response.items.map((typology) => ({
                        value: TypologyApi.toResourceIRI(typology.id),
                        label: typology.name,
                    }));
                    setTypologyOptions(options);
                }
            })
            .finally(() => setLoadingTypology(false));
    };

    const resetForm = () => {
        setFilterData({});
        setSelectedCompanies([]);
        setSelectedTraining(undefined);
        setSelectedTypologies([]);
        reset(defaultValues);
    };

    useEffect(() => {
        if (isInit) {
            loadTrainingOptions();
        }
    }, [isInit]);

    useEffect(() => {
        if (selectedTraining) {
            loadTypologyOptions();
        }
    }, [selectedTraining]);

    return (
        <AppFilterContainer
            onReset={resetForm}
            triggerElement={children}
            isModalOpen={isModalOpen}
            setIsInit={setIsInit}
            zIndex={"1400"}
        >
            <form
                id="app-filter-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={4} alignItems={"stretch"} justify={"start"}>
                    <AppFormControl label={t("cmn:label.search")}>
                        <Controller
                            control={control}
                            name={"internship_schedule_seat_search"}
                            defaultValue={filterData?.school_search || ""}
                            render={({ field }) => (
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <AppIcon name="ics-search" w="1rem" />
                                    </InputLeftElement>
                                    <Input {...field} />
                                </InputGroup>
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.User:company.label")}>
                        <AppMultiSelector
                            onSelect={(data) => {
                                setSelectedCompanies(data);
                                setIsModalOpen(false);
                            }}
                            loadOptions={loadCompanyOptions}
                            selectedOpt={selectedCompanies}
                            subject={t("ent.User:company.label")}
                            triggerElement={
                                <Button
                                    size={"sm"}
                                    variant={"secondary"}
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    {t("padm.UserForm:button.selectCompany")}
                                </Button>
                            }
                            isModal={true}
                            isSearch={true}
                            selectorRows={8}
                            maxSelectOptions={undefined}
                            setIsModalOpen={setIsModalOpen}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.Internship:training.label")}>
                        <Controller
                            control={control}
                            name="training"
                            defaultValue={selectedTraining?.value as string}
                            render={({ field }) => (
                                <AppSelect<AppSelectOption>
                                    name={field.name}
                                    isClearable
                                    isLoading={loadingTraining}
                                    options={trainingOptions}
                                    value={selectedTraining || null}
                                    placeholder={t(
                                        "ent.Internship:training.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(e?.value || null);
                                        setSelectedTraining(
                                            e as AppSelectOption
                                        );
                                        setSelectedTypologies([]);
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.Internship:typology.label")}>
                        <Controller
                            control={control}
                            name="typology"
                            render={({ field }) => (
                                <AppSelect<AppSelectOption, true>
                                    name={field.name}
                                    isClearable
                                    isMulti={true}
                                    isLoading={loadingTypology}
                                    options={typologyOptions}
                                    value={selectedTypologies || null}
                                    placeholder={t(
                                        "ent.Internship:typology.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(
                                            e?.map((option) => option?.value) ||
                                                []
                                        );
                                        setSelectedTypologies(
                                            e as AppSelectOption[]
                                        );
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <SimpleGrid
                        columns={{ sm: 1, md: 2 }}
                        spacingX={5}
                        spacingY={4}
                        w={"full"}
                    >
                        <AppFormControl
                            label={t(
                                "padm.MyInternshipPage:startDateRange.label"
                            )}
                        >
                            <Controller
                                control={control}
                                name="startDateRange"
                                defaultValue={[
                                    filterData[
                                        "internshipSchedule.startDate[after]"
                                    ],
                                    filterData[
                                        "internshipSchedule.startDate[before]"
                                    ],
                                ]}
                                render={({ field }) => (
                                    <AppDatePicker
                                        selectsRange={true}
                                        startDate={field.value?.[0] ?? null}
                                        endDate={field.value?.[1] ?? null}
                                        onChange={(update) =>
                                            field.onChange(update)
                                        }
                                        placeholderText={t(
                                            "padm.MyInternshipPage:startDateRange.placeholder"
                                        )}
                                        dateFormat={APPC.APP_DP_DATE_FORMAT}
                                        customInput={<Input />}
                                        isClearable={true}
                                    />
                                )}
                            />
                        </AppFormControl>
                        <AppFormControl
                            label={t(
                                "padm.MyInternshipPage:endDateRange.label"
                            )}
                        >
                            <Controller
                                control={control}
                                name="endDateRange"
                                defaultValue={[
                                    filterData[
                                        "internshipSchedule.endDate[after]"
                                    ],
                                    filterData[
                                        "internshipSchedule.endDate[before]"
                                    ],
                                ]}
                                render={({ field }) => (
                                    <AppDatePicker
                                        selectsRange={true}
                                        startDate={field.value?.[0] ?? null}
                                        endDate={field.value?.[1] ?? null}
                                        onChange={(update) =>
                                            field.onChange(update)
                                        }
                                        placeholderText={t(
                                            "padm.MyInternshipPage:endDateRange.placeholder"
                                        )}
                                        dateFormat={APPC.APP_DP_DATE_FORMAT}
                                        customInput={<Input />}
                                        isClearable={true}
                                    />
                                )}
                            />
                        </AppFormControl>
                    </SimpleGrid>
                </VStack>
            </form>
        </AppFilterContainer>
    );
};
